import React, { useState } from 'react';
import { contactUsData } from '../services/CommonService';
import Swal from 'sweetalert2';
import './pages.css'

const ContactPage = ({ organizations = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      const alphabetOnly = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData({
        ...formData,
        [name]: alphabetOnly
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile.trim())) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData?.description?.trim()) {
      newErrors.description = 'description is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      Swal.fire({
        title: 'Form Incomplete',
        text: 'Please fill all required fields correctly.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return
    }
    setIsSubmitting(true);

    try {
      formData.status = 1;
      // In a real app, you would send data to your backend
      const response = await contactUsData(formData);
      // Show success message using SweetAlert (if available)
      if (response.status == 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Your message has been sent successfully.',
          icon: 'success',
          confirmButtonText: 'OK'
        });
      } else {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
      }

      // Reset form
      setFormData({
        name: '',
        mobile: '',
        email: '',
        description: ''
      });
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || err?.message,
        icon: "error",
        confirmButtonText: "OK",
      });

    } finally {
      setIsSubmitting(false);
    }
  };

  // Default organization if none provided
  const defaultOrg = {
    name: "ACWADAM",
    fullform: "(Advanced Center for Water Resources Development and Management)",
    location: "",
    address: "ACWADAM, Lenyadri Society, Pune - 411052, Maharashtra, India.",
    corresAdd: "ACWADAM, BELAS, Plot No. 5, Kulashree Society, Karve Nagar, Pune - 411052.",
    phone: "+91-9172246959",
    fax: "+91-80-22263045",
    email: "admin@acwadam.org",
    website: "admin@acwadam.org"
  };

  const orgList = organizations.length > 0 ? organizations : [defaultOrg];

  return (
    <div className="container-fluid p-0">
      {/* Success Alert */}
      {submitted && (
        <div className="container mt-3">
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <strong>Success!</strong> Your message has been sent successfully.
            <button
              type="button"
              className="btn-close"
              onClick={() => setSubmitted(false)}
              aria-label="Close"
            ></button>
          </div>
        </div>
      )}

      <div className="container px-5 contact mt-4 mb-5 pb-3" style={{ height: 'calc(100vh - 80px)' }}>
        <div className="row">
          {/* Contact Form */}
          <div className="col-lg-5">
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="name" className='required'>
                  Name
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>

              <div className="mb-3">
                <label htmlFor="mobile" className='required'>
                  Mobile No.
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.mobile ? 'is-invalid' : ''}`}
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                />
                {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
              </div>

              <div className="mb-3">
                <label htmlFor="email" className='required'>Email ID</label>
                <input
                  type="email"
                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>

              <div className="mb-3">
                <label htmlFor="description" className='required'>Description</label>
                <textarea
                  className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                  rows="4"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                ></textarea>
                {errors.description && <div className="invalid-feedback">{errors.description}</div>}
              </div>
              <div className="text-start">
                <button
                  type="submit"
                  className="btn btn-warning rounded-pill px-4"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Sending...
                    </>
                  ) : 'Send'}
                </button>
              </div>
            </form>
          </div>

          {/* Organization Details */}
          <div className="card col-lg-7 p-3">
            {orgList.map((org, index) => (
              <div key={`details-${index}`} className="mb-4">
                <h6 className="mb-3 d-flex">
                  <b className="me-1">{org.name}</b>{org.fullform}</h6>
                <ul className="list-unstyled">
                  <li className="mb-2">
                    <i className="bi bi-geo-alt me-2 text-warning"></i>
                    <strong>Registered Address:</strong> {org.address}
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-house-door me-2 text-warning"></i>
                    <strong>Correspondence Address:</strong> {org.corresAdd}
                  </li><br />
                  <li className="mb-2">
                    <i className="bi bi-telephone me-2 text-warning"></i>
                    <strong>Phone:</strong> {org.phone}
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-envelope me-2 text-warning"></i>
                    <strong>Email:</strong> {org.email}
                  </li>
                </ul>
              </div>
            ))}

            {/* Second organization */}
            {/* {orgList.length === 1 && (
              <div className="mb-4">
                <h4 className="mb-3">Water Resources Department, Govt of Karnataka -</h4>
                <ul className="list-unstyled">
                  <li className="mb-2">
                    <strong>Address:</strong> Room No.105/204, 3rd Floor, Vikasa Soudha, Bengaluru-560001
                  </li>
                  <li className="mb-2">
                    <strong>Phone:</strong> +91-80-22255324, 22257064
                  </li>
                  <li className="mb-2">
                    <strong>Email:</strong> prs-wrg@karnataka.gov.in, secy-wrg@karnataka.gov.in
                  </li>
                </ul>
              </div>
            )} */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;